import { useMutation } from '@apollo/client'
import { AlertTriangle, Loader } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { use } from 'react'
import { useForm } from 'react-hook-form'
import { Link, Navigate, useSearchParams } from 'react-router'
import { AuthContext } from '@/components/auth/context'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { RECAPTCHA_ACTION_LOGIN } from '@/config/recaptcha'
import { gql } from '@/lib/graphql/gql'
import { useRecaptcha } from '@/lib/hooks'
import { SevallaLogo } from '@/logos'
import type {
  InitiateSamlSsoLoginWithEmailMutation,
  InitiateSamlSsoLoginWithEmailMutationVariables,
} from '@/routes/login/sso/index.types.gen'

const INITIATE_SAML_SSO_LOGIN_WITH_EMAIL_MUTATION = gql`
  mutation InitiateSamlSsoLoginWithEmail(
    $email: String!
    $recaptchaToken: String
    $destination: String
    $originPath: String
  ) {
    initiateSamlSsoLogin(
      email: $email
      recaptchaToken: $recaptchaToken
      destination: $destination
      originPath: $originPath
    ) {
      id
      idCompany
      redirectUrl
      error
    }
  }
`

type SsoLoginInputs = {
  email: string
  acceptTerms?: boolean
}

const ERROR_MESSAGES: Record<string, string> = {
  access_denied: 'Access denied by identity provider.',
  invalid_response: 'Invalid SAML response from identity provider.',
  domain_mismatch:
    'Your email domain does not match the configured SSO domain.',
  user_not_found: 'User not found. JIT provisioning might be disabled.',
  config_not_found:
    'No SSO configuration found for this email domain. Please log in with your password.',
}

export const SsoLogin = () => {
  const [searchParams] = useSearchParams()
  const { isAuthenticated = false } = use(AuthContext) ?? {}
  const getRecaptchaToken = useRecaptcha()

  const destination = searchParams.get('destination') ?? '/'
  const urlError = searchParams.get('error')

  const [initiateSsoLogin, { loading, error: mutationError, data, reset }] =
    useMutation<
      InitiateSamlSsoLoginWithEmailMutation,
      InitiateSamlSsoLoginWithEmailMutationVariables
    >(INITIATE_SAML_SSO_LOGIN_WITH_EMAIL_MUTATION)

  const isJitRegistration = Boolean(
    data?.initiateSamlSsoLogin?.redirectUrl && !data?.initiateSamlSsoLogin?.id
  )

  const form = useForm<SsoLoginInputs>({
    defaultValues: {
      email: '',
      acceptTerms: false,
    },
  })

  const acceptTerms = form.watch('acceptTerms')
  const isSubmitDisabled = isJitRegistration && !acceptTerms

  const hasError =
    urlError || mutationError?.message || data?.initiateSamlSsoLogin?.error
  const errorMessage = urlError
    ? (ERROR_MESSAGES[urlError] ?? 'An error occurred during SSO login.')
    : mutationError?.message?.includes('No SAML configuration')
      ? ERROR_MESSAGES.config_not_found
      : (mutationError?.message ?? data?.initiateSamlSsoLogin?.error)

  const onSubmit = async ({
    email,
    acceptTerms: hasAcceptedTerms,
  }: SsoLoginInputs) => {
    const recaptchaToken = await getRecaptchaToken(RECAPTCHA_ACTION_LOGIN)

    const result = await initiateSsoLogin({
      variables: {
        email,
        recaptchaToken,
        destination,
        originPath: window.location.href,
      },
    })

    const response = result.data?.initiateSamlSsoLogin
    const redirectUrl = response?.redirectUrl
    const userId = response?.id

    const canRedirect = (userId || hasAcceptedTerms) && redirectUrl

    if (canRedirect) {
      window.location.href = redirectUrl
    }
  }

  if (isAuthenticated) {
    return <Navigate to={destination} replace />
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-4">
        <a
          href="https://sevalla.com"
          className="flex items-center gap-2 self-center"
        >
          <SevallaLogo className="w-24 h-auto" />
        </a>
        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="font-display text-xl">
                Single sign-on
              </CardTitle>
              <CardDescription>
                {isJitRegistration
                  ? 'Accept the terms to complete your registration.'
                  : 'Enter your work email to sign in with SSO.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-6">
                    {hasError && (
                      <AnimatePresence>
                        <motion.div
                          key="alert"
                          layout
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <Alert
                            variant="destructive"
                            className="mx-auto w-fit"
                          >
                            <AlertTriangle />
                            <AlertTitle>Authentication error</AlertTitle>
                            <AlertDescription>{errorMessage}</AlertDescription>
                          </Alert>
                        </motion.div>
                      </AnimatePresence>
                    )}

                    <FormField
                      control={form.control}
                      name="email"
                      rules={{
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email address',
                        },
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Work email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="you@company.com"
                              type="email"
                              autoComplete="email"
                              aria-label="Work email"
                              disabled={loading || isJitRegistration}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {isJitRegistration && (
                      <FormField
                        control={form.control}
                        name="acceptTerms"
                        render={({ field }) => (
                          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={loading}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="text-sm font-normal cursor-pointer">
                                I agree to Sevalla's{' '}
                                <a
                                  href="https://sevalla.com/legal/terms-of-service/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline underline-offset-4 hover:text-primary"
                                >
                                  Terms of Service
                                </a>{' '}
                                and{' '}
                                <a
                                  href="https://sevalla.com/legal/privacy-policy/"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="underline underline-offset-4 hover:text-primary"
                                >
                                  Privacy Policy
                                </a>
                              </FormLabel>
                            </div>
                          </FormItem>
                        )}
                      />
                    )}

                    <div className="flex gap-3">
                      {isJitRegistration && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            reset()
                            form.setValue('acceptTerms', false)
                          }}
                        >
                          Back
                        </Button>
                      )}
                      <Button
                        type="submit"
                        className="flex-1"
                        disabled={loading || isSubmitDisabled}
                      >
                        {loading && <Loader className="animate-spin" />}
                        Continue
                      </Button>
                    </div>

                    <div className="text-center text-sm">
                      <Link
                        to="/login"
                        className="text-muted-foreground hover:text-primary underline underline-offset-4"
                      >
                        Sign in with password instead
                      </Link>
                    </div>
                  </div>
                </form>
              </Form>

              <div className="mt-4 text-muted-foreground *:[a]:hover:text-primary text-center text-xs text-balance *:[a]:underline *:[a]:underline-offset-4">
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="https://policies.google.com/terms" target="_blank">
                  Terms of Service
                </a>{' '}
                apply.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default SsoLogin
